import _, { isFinite, range } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import uuidBase62 from 'uuid-base62';
import sanitize from 'sanitize-filename';
import { N_YEARS } from '../constants/years';

export const timeseriesArray = ({ start = 0, length }) =>
  Array.from({ length }).map((y, i) => i + start);

export const metricsQueryString = (metrics) =>
  metrics
    .map(({ accessor, parameters = [] }) => {
      if (parameters.length) {
        return `${accessor}(${parameters
          .map((parameter) => `${parameter}: $${parameter}`)
          .join(',')})`;
      }
      return accessor;
    })
    .join(' ');

export const objectWithFields = (fields) =>
  fields.reduce((acc, field) => ({ ...acc, [field]: undefined }), {});

/*
Sums an array of arrays by index, eg:
sumByIndex([[1, 1], [2, 3]]) = [3, 4]
*/
export const sumByIndex = (group) =>
  group.reduce(
    (result, array) => array.map((value, i) => value + (result[i] || 0)),
    [0],
  );

/* Returns a 30 year array with a given value */
export const thirtyYearArray = (value) =>
  Array.from({ length: N_YEARS }).map(() => value);

export const getFarmPath = (farmId, path) => `/app/farm/${farmId}/${path}`;

export const getIsFarmPath = (path) => {
  return path?.startsWith('/app/farm/');
};

export const getIsTeamPath = (path) => {
  return path?.startsWith('/app/team/');
};

export const navigateToPortal = ({ teamId, continueUrl, replace = false }) => {
  const path = `/billing/portal/${teamId}?url=${
    continueUrl || window.location.href
  }`;
  if (replace) {
    window.location.replace(path);
  } else {
    window.location.href = path;
  }
};

export const getRootUrl = () => {
  return window.location.origin;
};

export const filterRowByColumnValue = (
  row,
  columnAccessor,
  value,
  defaultValue = 'All',
) => {
  return (
    !value ||
    !columnAccessor ||
    value.toLowerCase() === defaultValue.toLowerCase() ||
    row[columnAccessor].toLowerCase() === value.toLowerCase()
  );
};

// Returns a unique name for an item, given a list of existing items with a name property
export const uniqueName = (nameString, existingItems) => {
  let newItemIndex = existingItems.length + 1;
  let name;

  const nameExists = (newName) =>
    existingItems.some((item) => item.name === newName);

  do {
    name = `${nameString} ${newItemIndex}`;
    newItemIndex += 1;
  } while (nameExists(name));

  return name;
};

// Return a name like "name Copy" or "name Copy (n)" where n is the next available number
export const copyName = (nameString, existingItems) => {
  let copyExists = false;
  let copyNumber = 0;
  const regex = new RegExp(`${nameString} Copy( \\((\\d+)\\))?$`);
  existingItems.forEach(({ name: existingName }) => {
    const match = existingName.match(regex);

    if (match) {
      copyExists = true;
      const number = match[2] ? parseInt(match[2], 10) : null;
      if (number !== null && number > copyNumber) {
        copyNumber = number;
      }
    }
  });
  if (!copyExists) {
    return `${nameString} Copy`;
  }
  return `${nameString} Copy (${copyNumber + 1})`;
};

/*
 * Checks whether the passed fieldname is a duplicate of the existing fieldname
 * Examples:
 * (field, field) => true
 * (field, field 1) => true
 * (field 1, field) => false
 * (Field, Blueberry Field) => false
 */
export const isDuplicateFieldname = (nameToCompare, existingFieldName) => {
  if (!existingFieldName.includes(nameToCompare)) return false;
  const existingFieldNameArray = existingFieldName.split(' ');
  return isFinite(parseInt(existingFieldNameArray.slice(-1)[0], 10));
};

// windows limits full file paths to 260 characters
// truncate to 200 characters to leave some room for the rest of the path
export const exportFileName = (name, extension) =>
  `${sanitize(name).slice(0, 200)}${extension ? `.${extension}` : ''}`;

/**
 * Either pads or truncates `array` to the given `length`. If needed, fills new elements with `fill`.
 *
 * Note: this returns a new array and does not mutate the input, but does not deeply clone it.
 */
export const padOrTruncateArray = (array, length, fill = null) =>
  array.concat(Array(length).fill(fill)).slice(0, length);

/**
 * Get the range of numbers between two numbers, inclusive.
 */
export const getRange = (a, b) => {
  // eslint-disable-next-line no-param-reassign
  if (a > b) [a, b] = [b, a];
  return range(a, b + 1);
};

/**
 * If `evt` is a shift-click, prevent it from causing a text selection.
 *
 * Should be used with an `onMouseDown` handler.
 *
 * @param {import('react').MouseEvent} evt
 */
export const preventShiftClickTextSelection = (evt) => {
  if (evt.shiftKey) {
    document.getSelection().removeAllRanges();
  }
};

/**
 * Returns true if user is using a Mac.
 */
export const isMac = () =>
  typeof navigator !== 'undefined' && navigator?.userAgent?.includes('Mac');

/**
 * Usage: `array.sort(alphanumericSort)` for an array of **strings**.
 *
 * NOTE: this does not correctly sort negative numbers within strings.
 */
export const alphanumericSort = new Intl.Collator('us', { numeric: true })
  .compare;

/**
 * Map of models to their database prefixes
 */
export const MODEL_PREFIXES = {
  SECTION: 'sec',
  LAYER: 'lay',
  LAYER_FEATURE: 'layft',
  NOTE: 'note',
};

/**
 * Generates an ID with the correct format for the Overyield database.
 * The given model should correspond to one of the entries defined in MODEL_PREFIXES.
 * Use this when you need to generate an ID on the frontend that will be persisted in the database.
 */
export const generateDbId = (model) => {
  const prefix = MODEL_PREFIXES[model];
  return `${prefix}_${uuidBase62.encode(uuidv4())}`;
};

export const farmCanBeEdited = (farm) => {
  const { hasValidSubscription, isArchived, isLocked } = farm || {};
  return (hasValidSubscription && !isArchived && !isLocked) || false;
};

export const farmCanBeDuplicated = (farm, isSuperadmin) =>
  (isSuperadmin || farmCanBeEdited(farm)) && !farm.isArchived;

/**
 * Remove all `keys` from `obj` without mutating and return the object.
 */
export const removeKeys = (obj, keys) => {
  const newObj = { ...obj };
  keys.forEach((key) => {
    delete newObj[key];
  });
  return newObj;
};

/**
 * Remove `key` from `obj` without mutating and return the object.
 */
export const removeKey = (obj, key) => {
  const newObj = { ...obj };
  delete newObj[key];
  return newObj;
};

/**
 * Divides `numerator` by `denominator`, returning `fallback` if `denominator` is 0.
 */
export const safeDivide = (numerator, denominator, fallback = 0) =>
  denominator === 0 ? fallback : numerator / denominator;

/**
 * Splits `array` into multiple chunks, determined by the given `indexes`.
 */
export const splitArrayByIndexes = (arr, indexes) => {
  const result = [];

  let start = 0;
  indexes.forEach((end) => {
    result.push(arr.slice(start, end));
    start = end;
  });
  result.push(arr.slice(start));

  return result.filter((chunk) => chunk.length > 0);
};

/**
 * Returns the symmetrical difference between two arrays.
 */
export const symmetricalDiff = (arr1, arr2) =>
  _.difference(arr1, arr2).concat(_.difference(arr2, arr1));

/**
 * Polls `fn` every `interval` ms until it returns a truthy value or `timeout`
 * ms have passed.
 */
export const waitForSuccess = async (fn, interval = 50, timeout = 2500) =>
  new Promise((resolve, reject) => {
    const start = Date.now();

    const check = async () => {
      try {
        const result = await fn();
        if (result) {
          resolve(result);
        } else if (Date.now() - start < timeout) {
          setTimeout(check, interval);
        } else {
          reject(new Error('waitForSuccess timed out'));
        }
      } catch (error) {
        reject(error);
      }
    };

    check();
  });

export const augmentFarmTags = (farm) => ({
  ...farm,
  tags: [
    ...(farm.isLocked ? ['Locked'] : []),
    ...(farm.isArchived ? ['Archived'] : []),
    ...farm.tags,
  ],
});

export const logicalXor = (a, b) => (a || b) && !(a && b);
